import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Typography from '@mui/material/Typography';
import LgmButton from './LgmButton';
import { useApi } from '../services/api/provider';
import { ProductService } from '../services/api/domains/products/service';
import type {
  CasQuarantineReviewRow,
  CasQuarantineSplitResponse,
} from '../services/api/domains/products/types';

/**
 * Split action panel for `manufacturer_name_merge` rows (issue #662,
 * CQR-019). Detection (CQR-010) deliberately makes no MERGE-1a
 * (genuinely different drugs, splittable) vs. MERGE-1b (same drug,
 * different salt/hydrate/stereo form, "do nothing" only) classification —
 * an explicit product decision to avoid any similarity-based assumption
 * about product identity (see the requirements doc's CQR-010 note). This
 * panel therefore always offers both the split flow and "Do nothing" side
 * by side, with copy steering a genuine MERGE-1b case toward "Do nothing"
 * rather than the panel silently deciding that for the admin.
 *
 * Re-link target resolution is discovered from the split endpoint itself,
 * not a separate preview call: submitting with just a `name` either
 * succeeds (unambiguous match or fork), or 409s with
 * `SPLIT_AMBIGUOUS_EXISTING_MATCH` + `candidate_product_ids`, at which
 * point this panel re-submits with an explicit `targetProductId`.
 */

interface ConflictOption {
  name: string;
  cas_number: string | null;
}

function getConflicts(row: CasQuarantineReviewRow): ConflictOption[] {
  const payload = row.raw_payload as { conflicts?: unknown } | null;
  if (!payload || !Array.isArray(payload.conflicts)) return [];
  return (payload.conflicts as ConflictOption[]).filter(
    c => typeof c?.name === 'string'
  );
}

type Phase =
  'selecting' | 'confirming' | 'ambiguous' | 'alreadyResolved' | 'success';

// Test 6 manual-test feedback, 2026-09-18: a split creates or
// deletes a real product record -- the admin needs to actually SEE which
// happened (forked vs. re-linked, and to which product id) before the page
// moves on, not have onResolved() silently auto-advance underneath them.
// Unlike confirm/reject (which only ever resolve THIS row), split's
// cross-product side effect warrants a deliberate pause.
function describeSplitOutcome(
  name: string,
  data: CasQuarantineSplitResponse
): string {
  if (data.relinked) {
    return `"${name}" re-linked to existing product #${data.new_product_id}.`;
  }
  if (data.new_product_id) {
    return `"${name}" moved onto a new product (#${data.new_product_id}).`;
  }
  return `"${name}" split completed.`;
}

// The split route short-circuits to this exact idempotent shape when the row
// was already resolved (docs/features/cas-quarantine-resolution.md line 463,
// "Concurrent review": the second submit finds the row already resolved and
// must show the OTHER admin's outcome, not silently treat this submit as
// having succeeded and auto-advance) -- same message string confirm/reject
// use (Copilot review, PR #792 round 9).
function isAlreadyResolvedElsewhere(data: { message?: string }): boolean {
  return data.message === 'Review entry already resolved';
}

interface CasMergeSplitPanelProps {
  productId: number;
  row: CasQuarantineReviewRow;
  onResolved: (info?: { productDeleted?: boolean }) => Promise<void>;
}

const CasMergeSplitPanel: React.FC<CasMergeSplitPanelProps> = ({
  productId,
  row,
  onResolved,
}) => {
  const api = useApi();
  const service = React.useMemo(() => new ProductService(api), [api]);

  const conflicts = getConflicts(row);
  const [phase, setPhase] = useState<Phase>('selecting');
  const [selectedName, setSelectedName] = useState<string | null>(
    conflicts.length === 1 ? conflicts[0].name : null
  );
  const [ambiguousCandidates, setAmbiguousCandidates] = useState<number[]>([]);
  const [selectedTargetId, setSelectedTargetId] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [didNothingAck, setDidNothingAck] = useState(false);
  const [alreadyResolvedStatus, setAlreadyResolvedStatus] = useState<
    string | null
  >(null);
  const [splitOutcome, setSplitOutcome] = useState<{
    message: string;
    hasRemainingConflicts: boolean;
    originalDeleted: boolean;
  } | null>(null);

  const handleDoNothing = () => setDidNothingAck(true);

  const handleAcknowledgeAlreadyResolved = async () => {
    setIsSubmitting(true);
    try {
      await onResolved();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinueAfterSplit = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await onResolved({ productDeleted: splitOutcome?.originalDeleted });
      setPhase('selecting');
      setSplitOutcome(null);
    } catch (err) {
      // The split itself already committed server-side -- only the
      // caller's own follow-up (e.g. a queue refetch) failed. Keep the
      // success screen up (not the stale "selecting" state, which would
      // let the admin resubmit a split that already happened) and let
      // them retry Continue (Copilot review, PR #802).
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to continue after the split -- the split itself already completed. Retry to refresh the queue.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitSplit = async (targetProductId?: number) => {
    if (!selectedName) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await service.splitQuarantineReviewRow(
        productId,
        row.review_id,
        {
          name: selectedName,
          ...(targetProductId ? { targetProductId } : {}),
        }
      );
      // Another admin may have already resolved this row (the split route's
      // idempotent short-circuit) -- surface THEIR recorded outcome instead
      // of treating this submit as a successful split and auto-advancing
      // past it (docs/features/cas-quarantine-resolution.md line 463;
      // Copilot review, PR #792 round 9).
      if (isAlreadyResolvedElsewhere(response.data)) {
        setAlreadyResolvedStatus(response.data.status);
        setPhase('alreadyResolved');
        return;
      }
      // A 3+-conflict row stays pending after removing just one name -- the
      // parent keeps rendering this SAME component (same review_id key), so
      // local workflow state (phase/selectedName/target) must be reset here
      // rather than surviving into the next conflict's decision. Reset
      // unconditionally: harmless when the row instead resolved fully, since
      // nothing re-renders this instance in that case anyway.
      setSelectedName(null);
      setAmbiguousCandidates([]);
      setSelectedTargetId(null);
      // Show what actually happened (forked vs. re-linked, and to which
      // product id) before calling onResolved -- a split creates/deletes a
      // real product, and onResolved's own refetch/auto-advance would
      // otherwise whisk the admin away with zero visible confirmation
      // (Test 6 feedback, 2026-09-18). onResolved fires only once
      // the admin clicks Continue (handleContinueAfterSplit), same
      // "pause on a real outcome" pattern as the alreadyResolved phase.
      setSplitOutcome({
        message: describeSplitOutcome(selectedName, response.data),
        hasRemainingConflicts:
          (response.data.remaining_conflicts?.length ?? 0) > 0,
        originalDeleted: !!response.data.original_deleted,
      });
      setPhase('success');
    } catch (err) {
      const response =
        typeof err === 'object' && err !== null && 'response' in err
          ? (err as { response?: { status?: number; data?: unknown } }).response
          : undefined;
      const data = response?.data as
        { code?: string; candidate_product_ids?: number[] } | undefined;
      if (
        response?.status === 409 &&
        data?.code === 'SPLIT_AMBIGUOUS_EXISTING_MATCH'
      ) {
        setAmbiguousCandidates(data.candidate_product_ids ?? []);
        setPhase('ambiguous');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to split');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (conflicts.length === 0) {
    return (
      <div style={styles.container}>
        <Typography variant="body2" style={styles.note}>
          No conflicting names recorded on this row — use &quot;Do nothing&quot;
          or review the raw source data above.
        </Typography>
        <div style={styles.actionRow}>
          <LgmButton onClick={handleDoNothing}>Do nothing</LgmButton>
        </div>
        {didNothingAck && (
          <span style={styles.ackText}>Left pending — no changes made.</span>
        )}
      </div>
    );
  }

  if (phase === 'alreadyResolved') {
    return (
      <div style={styles.container}>
        <Alert severity="info">
          {/* Actor-neutral: the same admin retrying after a lost response
              hits this same idempotent path, so "another admin" would be
              false in that case (Copilot review, PR #792, post-merge round). */}
          This item has already been resolved
          {alreadyResolvedStatus
            ? ` — recorded outcome: ${alreadyResolvedStatus}`
            : ''}
          . Your submitted split was not applied.
        </Alert>
        <div style={styles.actionRow}>
          <LgmButton
            onClick={handleAcknowledgeAlreadyResolved}
            disabled={isSubmitting}>
            Continue
          </LgmButton>
        </div>
      </div>
    );
  }

  if (phase === 'success' && splitOutcome) {
    return (
      <div style={styles.container}>
        <Alert severity="success">
          {splitOutcome.message}
          {splitOutcome.hasRemainingConflicts
            ? ' This row still has other conflicting names pending.'
            : ''}
        </Alert>
        {error && <Alert severity="error">{error}</Alert>}
        <div style={styles.actionRow}>
          <LgmButton onClick={handleContinueAfterSplit} disabled={isSubmitting}>
            {isSubmitting ? 'Continuing…' : 'Continue'}
          </LgmButton>
        </div>
      </div>
    );
  }

  if (phase === 'ambiguous') {
    return (
      <div style={styles.container}>
        <Alert severity="info">
          Multiple existing products match &quot;{selectedName}&quot; — pick
          which one to re-link to.
        </Alert>
        {error && <Alert severity="error">{error}</Alert>}
        <RadioGroup
          value={selectedTargetId === null ? '' : String(selectedTargetId)}
          onChange={e => setSelectedTargetId(Number(e.target.value))}>
          {ambiguousCandidates.map(id => (
            <FormControlLabel
              key={id}
              value={String(id)}
              control={<Radio size="small" disabled={isSubmitting} />}
              label={`Re-link to existing product ${id}`}
            />
          ))}
        </RadioGroup>
        <div style={styles.actionRow}>
          <LgmButton
            disabled={selectedTargetId === null || isSubmitting}
            onClick={() => submitSplit(selectedTargetId ?? undefined)}>
            {isSubmitting ? 'Splitting…' : 'Confirm re-link'}
          </LgmButton>
          <LgmButton
            disabled={isSubmitting}
            onClick={() => {
              setPhase('selecting');
              setSelectedTargetId(null);
              setError(null);
              setDidNothingAck(false);
            }}>
            Back
          </LgmButton>
        </div>
      </div>
    );
  }

  if (phase === 'confirming' && selectedName) {
    const conflict = conflicts.find(c => c.name === selectedName);
    return (
      <div style={styles.container}>
        {error && <Alert severity="error">{error}</Alert>}
        <Alert severity="warning">
          This will move &quot;{selectedName}&quot;
          {conflict?.cas_number ? ` (CAS ${conflict.cas_number})` : ''} onto an
          existing matching product, or a new one if none matches. This cannot
          be undone from here.
        </Alert>
        <div style={styles.actionRow}>
          <LgmButton disabled={isSubmitting} onClick={() => submitSplit()}>
            {isSubmitting ? 'Splitting…' : 'Confirm split'}
          </LgmButton>
          <LgmButton
            disabled={isSubmitting}
            onClick={() => {
              setPhase('selecting');
              setDidNothingAck(false);
            }}>
            Back
          </LgmButton>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Typography variant="body2" style={styles.note}>
        If this is the same drug in a different salt or hydrate form (not a
        genuinely different product), use &quot;Do nothing&quot; — support for a
        single product carrying multiple CAS numbers isn&apos;t built yet.
      </Typography>
      <RadioGroup
        value={selectedName ?? ''}
        onChange={e => setSelectedName(e.target.value)}>
        {conflicts.map(conflict => (
          <FormControlLabel
            key={conflict.name}
            value={conflict.name}
            control={<Radio size="small" />}
            label={`${conflict.name}${conflict.cas_number ? ` (CAS ${conflict.cas_number})` : ''}`}
          />
        ))}
      </RadioGroup>
      <div style={styles.actionRow}>
        <LgmButton
          disabled={!selectedName}
          onClick={() => setPhase('confirming')}>
          Split selected name
        </LgmButton>
        <LgmButton onClick={handleDoNothing}>Do nothing</LgmButton>
      </div>
      {didNothingAck && (
        <span style={styles.ackText}>Left pending — no changes made.</span>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  actionRow: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' },
  note: { color: '#64748b' },
  ackText: { color: '#64748b', fontStyle: 'italic', fontSize: '0.85rem' },
};

export default CasMergeSplitPanel;
